import type { CardType, Chapter, PlotCard, PlotLine, TimelineState } from '@/pages/timeline/types';
import { supabase } from './supabase';

// Add this flag at the top of the file after the imports
// Set to true to reduce console logs for repeated operations
const minimalLogs = true;

export interface Timeline {
    id: string;
    title: string;
    description?: string;
    created_at: string;
    updated_at: string;
    is_primary: boolean;
}

export interface TimelineSettings {
    id: string;
    timeline_id: string;
    zoom_level: number;
    display_mode: 'vertical' | 'horizontal';
    show_chapter_markers: boolean;
    color_scheme: Record<string, string>;
}

// Default chapters to use if none are found
const defaultChapters: Chapter[] = [
    {
        id: -1, // Use negative IDs to avoid conflicts with real chapters
        name: 'Chapter 1: Introduction',
        description: 'The beginning of the story',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    },
    {
        id: -2,
        name: 'Chapter 2: Rising Action',
        description: 'The plot thickens',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    },
    {
        id: -3,
        name: 'Chapter 3: Climax',
        description: 'The turning point',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
    }
];

// In-memory storage for mock chapters and cards
const mockChapters: Chapter[] = [...defaultChapters];
const mockCards: PlotCard[] = [];

// Add these interfaces at the top of the file, after the imports
interface DBChapter {
    id: number;
    title: string;
    description?: string;
    created_at: string;
    last_saved_at?: string;
    story_id: number;
    tenant_id: string;
    content_url?: string;
    content_type?: string;
    version?: number;
    display_order?: number;
}

interface DBPlotCard {
    id: number;
    title: string;
    description?: string;
    created_at: string;
    updated_at: string;
    timeline_id: number;
    chapter_id?: number;
    tenant_id: string;
    event_date?: string;
    position?: number;
    color?: string;
    tags?: string[];
    metadata?: {
        card_type?: CardType;
        [key: string]: any;
    };
}

class TimelineService {
    async getTimeline(projectId: string, storyId?: string): Promise<TimelineState> {
        try {
            if (!minimalLogs) {
                console.log('=== Fetching timeline data for project:', projectId);
            }

            // Use the new single-call method to get all timeline data at once
            return this.getTimelineDataSingleCall(projectId);

        } catch (error) {
            console.error('Error in getTimeline:', error);
            throw error;
        }
    }

    private async createDefaultSettings(timelineId: string): Promise<TimelineSettings> {
        try {
            const defaultSettings = {
                timeline_id: timelineId,
                zoom_level: 1,
                display_mode: 'horizontal' as const,
                show_chapter_markers: true,
                color_scheme: {
                    'character': '#4299e1',
                    'event': '#ed8936',
                    'location': '#48bb78',
                    'item': '#9f7aea',
                    'note': '#f6e05e'
                }
                // tenant_id is automatically set by RLS policies using auth.jwt()
            };

            const { data, error } = await supabase
                .from('timeline_settings')
                .insert([defaultSettings])
                .select()
                .single();

            if (error) {
                console.error('Error creating timeline settings:', error);

                // If it's an RLS policy violation, return a default settings object
                // This allows the app to continue functioning even if DB write fails
                if (error.code === '42501') {
                    console.warn('RLS policy violation, returning default settings object');
                    return {
                        id: 'temp-' + Date.now(),
                        timeline_id: timelineId,
                        zoom_level: 1,
                        display_mode: 'horizontal' as const,
                        show_chapter_markers: true,
                        color_scheme: {
                            'character': '#4299e1',
                            'event': '#ed8936',
                            'location': '#48bb78',
                            'item': '#9f7aea',
                            'note': '#f6e05e'
                        }
                    };
                }

                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in createDefaultSettings:', error);

            // Return default settings as fallback
            const defaultSettings = {
                id: 'temp-' + Date.now(),
                timeline_id: timelineId,
                zoom_level: 1,
                display_mode: 'horizontal' as const,
                show_chapter_markers: true,
                color_scheme: {
                    'character': '#4299e1',
                    'event': '#ed8936',
                    'location': '#48bb78',
                    'item': '#9f7aea',
                    'note': '#f6e05e'
                }
            };

            return defaultSettings;
        }
    }

    async getTimelineSettings(timelineId: string): Promise<TimelineSettings | null> {
        return this.getTimelineSettingsNoCache(timelineId);
    }

    async getTimelineSettingsNoCache(timelineId: string): Promise<TimelineSettings | null> {
        try {
            if (!minimalLogs) {
                console.log('Fetching timeline settings for timeline:', timelineId);
            }

            // Try to fetch existing timeline settings
            const { data, error } = await supabase
                .from('timeline_settings')
                .select('*')
                .eq('timeline_id', timelineId)
                .maybeSingle(); // Use maybeSingle instead of single to avoid PGRST116 error

            if (error) {
                console.error('Error fetching timeline settings:', error);
                throw error;
            }

            // If we found settings, return them
            if (data) {
                return data;
            }

            // Instead of creating settings automatically, return null
            if (!minimalLogs) {
                console.log('No settings found for timeline');
            }
            return null;
        } catch (error) {
            console.error('Error in getTimelineSettingsNoCache:', error);

            // Return null instead of default settings
            return null;
        }
    }

    async getPlotLines(timelineId: string): Promise<PlotLine[]> {
        try {
            console.log('Fetching plot lines for timeline:', timelineId);

            // Get the timeline itself first
            const { data: specificTimeline, error: timelineError } = await supabase
                .from('timelines')
                .select('*')
                .eq('id', timelineId)
                .maybeSingle();

            if (timelineError) {
                console.error('Error fetching specific timeline:', timelineError);
                throw timelineError;
            }

            // Now get all timelines for the story_id to enable reordering functionality
            if (specificTimeline) {
                const { data: allTimelines, error: allTimelinesError } = await supabase
                    .from('timelines')
                    .select('*')
                    .eq('story_id', specificTimeline.story_id)
                    .order('created_at', { ascending: true }); // Order by created_at as default

                if (allTimelinesError) {
                    console.error('Error fetching all timelines:', allTimelinesError);
                    throw allTimelinesError;
                }

                // If we found multiple timelines, convert them all to PlotLine objects
                if (allTimelines && allTimelines.length > 0) {
                    console.log(`Found ${allTimelines.length} timelines for story ID ${specificTimeline.story_id}`);

                    // Convert all timelines to PlotLine objects
                    const plotLines: PlotLine[] = allTimelines.map(timeline => ({
                        id: parseInt(timeline.id),
                        name: timeline.title || 'Untitled',
                        description: timeline.description || '',
                        color: '#4299e1', // Default color
                        created_at: timeline.created_at,
                        updated_at: timeline.updated_at,
                        _order: null // Will be populated if ordered previously
                    }));

                    return plotLines;
                }
            }

            // Fallback to just returning the specified timeline as a plot line
            if (!specificTimeline) {
                console.warn('Timeline not found, returning empty array');
                return [];
            }

            // Create a plot line from the specific timeline
            const plotLine: PlotLine = {
                id: parseInt(specificTimeline.id),
                name: specificTimeline.title,
                description: specificTimeline.description || '',
                color: '#4299e1', // Default color
                created_at: specificTimeline.created_at,
                updated_at: specificTimeline.updated_at
            };

            return [plotLine];
        } catch (error) {
            console.error('Error in getPlotLines:', error);
            throw error;
        }
    }

    async getPlotCards(timelineId: string): Promise<PlotCard[]> {
        return this.getPlotCardsNoCache(timelineId);
    }

    async getPlotCardsNoCache(timelineId: string): Promise<PlotCard[]> {
        try {
            if (!minimalLogs) {
                console.log('Fetching plot cards for timeline:', timelineId);
            }

            // Use the plotcards table with a more efficient query
            const { data, error } = await supabase
                .from('plotcards')
                .select('id, title, description, metadata, timeline_id, chapter_id, position, created_at, updated_at, color, tags')
                .eq('timeline_id', parseInt(timelineId)) // Convert to number for comparison
                .order('chapter_id', { ascending: true })
                .order('position', { ascending: true }) // Order by position first
                .order('created_at', { ascending: true }); // Then by creation date

            if (error) {
                console.error('Error fetching plot cards:', error);
                throw error;
            }

            if (!data || data.length === 0) {
                if (!minimalLogs) {
                    console.log('No plot cards found for timeline:', timelineId);
                }
                return [];
            }

            if (!minimalLogs) {
                console.log('Raw plot cards data count:', data.length);
            }

            // Transform the data to match the PlotCard interface
            const plotCards: PlotCard[] = data.map(item => {
                const metadata = item.metadata || {};
                const plotLineId = metadata.plotLineId || item.timeline_id?.toString() || '0';
                const cardType = metadata.type || 'note';

                return {
                    id: item.id,
                    title: item.title || 'Untitled',
                    content: item.description || '',
                    type: cardType as CardType,
                    x: parseInt(plotLineId),
                    y: item.chapter_id || 0,
                    value: item.description || '',
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    chapter_id: item.chapter_id || null,
                    position: item.position || null
                };
            });

            if (!minimalLogs) {
                console.log(`Found ${plotCards.length} plot cards for timeline ${timelineId}`);
            }
            return plotCards;
        } catch (error) {
            console.error('Error in getPlotCardsNoCache:', error);
            throw error;
        }
    }

    async createPlotLine(timelineId: string, title: string, color: string): Promise<PlotLine> {
        try {
            // Use the timelines table instead of plot_lines
            const { data, error } = await supabase
                .from('timelines')
                .insert([
                    {
                        story_id: timelineId,
                        title: title,
                        description: '',
                        is_primary: false,
                        // tenant_id is automatically set by RLS policies using auth.jwt()
                    }
                ])
                .select()
                .single();

            if (error) {
                console.error('Error creating plot line:', error);
                throw error;
            }

            // Transform the data to match the PlotLine interface
            const plotLine: PlotLine = {
                id: data.id,
                name: data.title || 'Untitled',
                description: data.description || '',
                color: color || '#4299e1',
                created_at: data.created_at,
                updated_at: data.updated_at
            };

            return plotLine;
        } catch (error) {
            console.error('Error in createPlotLine:', error);
            throw error;
        }
    }

    async createPlotCard(
        timelineId: string,
        plotLineId: string,
        title: string,
        content: string,
        cardType: CardType,
        chapterId?: number, // Add chapter ID parameter
        position?: number // Add position parameter
    ): Promise<PlotCard> {
        try {
            console.log('=== Creating plot card with params:', {
                timelineId,
                plotLineId,
                title,
                content,
                cardType,
                chapterId,
                position
            });

            // Since timelines and plot lines are the same, use plotLineId as the timeline_id
            // This ensures the card is associated with the correct timeline/plot line
            const actualTimelineId = plotLineId;
            console.log("=== Creating Plot Card with params:", {
                timelineId,
                plotLineId,
                title,
                content,
                cardType,
                chapterId,
                position
            });

            console.log('=== Using actual timeline ID:', actualTimelineId);

            // If position is not provided and chapterId is, get the highest position in the chapter
            let cardPosition = position;
            if (cardPosition === undefined && chapterId) {
                try {
                    const chapterCards = await this.getPlotCardsByChapter(chapterId, actualTimelineId);
                    // Find the highest position
                    const maxPosition = chapterCards.reduce((max, card) => {
                        const cardPos = card.position || 0;
                        return cardPos > max ? cardPos : max;
                    }, -1);
                    // Set the new card's position to be after the last card
                    cardPosition = maxPosition + 1;
                    console.log(`=== Setting card position to ${cardPosition} based on existing cards`);
                } catch (error) {
                    console.error('=== Error getting chapter cards for position:', error);
                    // Default to position 0 if there's an error
                    cardPosition = 0;
                }
            } else if (cardPosition === undefined) {
                // Default position if no chapter or position provided
                cardPosition = 0;
            }

            // Use the plotcards table
            const { data, error } = await supabase
                .from('plotcards')
                .insert([
                    {
                        timeline_id: actualTimelineId,
                        title,
                        description: content,
                        metadata: {
                            type: cardType,
                            plotLineId
                        },
                        chapter_id: chapterId || null, // Set chapter_id if provided
                        position: cardPosition // Set position
                        // tenant_id is automatically set by RLS policies using auth.jwt()
                    }
                ])
                .select()
                .single();

            if (error) {
                console.error('=== Error creating plot card:', error);
                throw error;
            }

            // Transform the data to match the PlotCard interface
            const cardData = data as DBPlotCard;
            const metadata = cardData.metadata || {};

            const plotCard: PlotCard = {
                id: cardData.id,
                title: cardData.title || 'Untitled',
                content: cardData.description || '',
                type: cardType,
                x: parseInt(plotLineId),
                y: cardData.chapter_id || 0, // Use chapter_id for y-coordinate in the frontend
                value: cardData.description || '', // For backward compatibility
                created_at: cardData.created_at,
                updated_at: cardData.updated_at,
                chapter_id: cardData.chapter_id || null, // Add chapter_id explicitly
                position: cardData.position || null
            };

            console.log('=== Created plot card:', plotCard);

            return plotCard;
        } catch (error) {
            console.error('=== Error in createPlotCard:', error);
            throw error;
        }
    }

    async updatePlotCard(
        cardId: string,
        updates: Partial<PlotCard>,
        timelineId?: string // Add timelineId parameter
    ): Promise<PlotCard> {
        try {
            console.log('=== Updating plot card:', cardId, 'with updates:', updates);

            // First, fetch the current card to get its timeline_id and existing metadata
            const { data: currentCard, error: fetchError } = await supabase
                .from('plotcards')
                .select('*')
                .eq('id', cardId)
                .single();

            if (fetchError) {
                console.error('Error fetching current card:', fetchError);
                throw fetchError;
            }

            if (!currentCard) {
                throw new Error(`Card with ID ${cardId} not found`);
            }

            const existingMetadata = currentCard.metadata || {};
            timelineId = timelineId || currentCard.timeline_id?.toString();

            // Track if chapter_id is changing for cache clearing
            const isChapterChanging =
                (updates.chapter_id !== undefined && updates.chapter_id !== currentCard.chapter_id) ||
                (updates.y !== undefined && updates.y !== currentCard.chapter_id);

            const oldChapterId = currentCard.chapter_id;

            console.log('=== Current card data:', currentCard);
            console.log('=== Existing metadata:', existingMetadata);
            console.log('=== Is chapter changing:', isChapterChanging, 'from', oldChapterId, 'to', updates.chapter_id || updates.y);

            // Prepare the updates for the database
            // Preserve existing metadata by merging with new updates
            const dbUpdates: any = {
                metadata: { ...existingMetadata }
            };

            // Map the PlotCard fields to database fields
            if (updates.title !== undefined) dbUpdates.title = updates.title;
            if (updates.content !== undefined) dbUpdates.description = updates.content;
            if (updates.type !== undefined) dbUpdates.metadata.type = updates.type;
            if (updates.x !== undefined) dbUpdates.metadata.plotLineId = updates.x.toString();

            // Handle y-coordinate as chapter_id
            if (updates.y !== undefined) dbUpdates.chapter_id = updates.y;

            // Also handle direct chapter_id updates
            if (updates.chapter_id !== undefined) dbUpdates.chapter_id = updates.chapter_id;

            console.log('=== DB updates to apply:', dbUpdates);

            // Update the card
            const { data: updatedCard, error: updateError } = await supabase
                .from('plotcards')
                .update(dbUpdates)
                .eq('id', cardId)
                .select()
                .single();

            if (updateError) {
                console.error('Error updating plot card:', updateError);
                throw updateError;
            }

            if (!updatedCard) {
                throw new Error('Failed to update plot card');
            }

            console.log('=== Updated card data from DB:', updatedCard);

            // Verify the update by fetching the latest data
            const { data: verifiedCard, error: verifyError } = await supabase
                .from('plotcards')
                .select('*')
                .eq('id', cardId)
                .single();

            if (verifyError) {
                console.error('Error verifying card update:', verifyError);
                throw verifyError;
            }

            if (!verifiedCard) {
                throw new Error('Failed to verify card update');
            }

            console.log('=== Verified card data:', verifiedCard);

            // Transform the verified data to match the PlotCard interface
            const metadata = verifiedCard.metadata || {};
            const plotLineId = metadata.plotLineId || verifiedCard.timeline_id?.toString() || '0';
            const cardType = metadata.type || 'note';

            const plotCard: PlotCard = {
                id: verifiedCard.id,
                title: verifiedCard.title || 'Untitled',
                content: verifiedCard.description || '',
                type: cardType as CardType,
                x: parseInt(plotLineId),
                y: verifiedCard.chapter_id || 0, // Use chapter_id for y-coordinate
                value: verifiedCard.description || '', // For backward compatibility
                created_at: verifiedCard.created_at,
                updated_at: verifiedCard.updated_at,
                chapter_id: verifiedCard.chapter_id || null, // Add chapter_id explicitly
                position: verifiedCard.position || null
            };

            console.log('=== Returning verified plot card:', plotCard);
            return plotCard;
        } catch (error) {
            console.error('Error in updatePlotCard:', error);
            throw error;
        }
    }

    async deletePlotCard(cardId: string, timelineId: string): Promise<void> {
        try {
            // First, get the card to know its chapter_id
            const { data: card, error: fetchError } = await supabase
                .from('plotcards')
                .select('chapter_id')
                .eq('id', cardId)
                .single();

            if (fetchError && fetchError.code !== 'PGRST116') {
                console.error('Error fetching card before deletion:', fetchError);
                // Continue with deletion even if fetch fails
            }

            // Use the plotcards table
            const { error } = await supabase
                .from('plotcards')
                .delete()
                .eq('id', cardId);

            if (error) {
                console.error('Error deleting plot card:', error);
                throw error;
            }

            console.log('=== Successfully deleted plot card');
        } catch (error) {
            console.error('Error in deletePlotCard:', error);
            throw error;
        }
    }

    async updatePlotLine(
        lineId: string,
        updates: Partial<PlotLine>
    ): Promise<PlotLine> {
        try {
            // Prepare the updates for the database
            const dbUpdates: any = {};

            // Map the PlotLine fields to database fields
            if (updates.name !== undefined) dbUpdates.title = updates.name;
            if (updates.description !== undefined) dbUpdates.description = updates.description;

            // Use the timelines table instead of plot_lines
            const { data, error } = await supabase
                .from('timelines')
                .update(dbUpdates)
                .eq('id', lineId)
                .select()
                .single();

            if (error) {
                console.error('Error updating plot line:', error);
                throw error;
            }

            // Transform the data to match the PlotLine interface
            const plotLine: PlotLine = {
                id: data.id,
                name: data.title || 'Untitled',
                description: data.description || '',
                color: '#4299e1', // Default color
                created_at: data.created_at,
                updated_at: data.updated_at
            };

            return plotLine;
        } catch (error) {
            console.error('Error in updatePlotLine:', error);
            throw error;
        }
    }

    async deletePlotLine(lineId: string, timelineId: string): Promise<void> {
        try {
            // Use the timelines table instead of plot_lines
            const { error } = await supabase
                .from('timelines')
                .delete()
                .eq('id', lineId);

            if (error) {
                console.error('Error deleting plot line:', error);
                throw error;
            }
        } catch (error) {
            console.error('Error in deletePlotLine:', error);
            throw error;
        }
    }

    async updateTimelineSettings(
        timelineId: string,
        updates: Partial<TimelineSettings>
    ): Promise<TimelineSettings> {
        try {
            console.log('Updating timeline settings for timeline:', timelineId);

            // First, check if settings exist
            const existingSettings = await this.getTimelineSettings(timelineId);

            // If settings don't exist, create them first
            if (!existingSettings) {
                console.log('No settings found, creating default settings before updating');
                await this.createTimelineSettings(timelineId);
            }

            // Now update the settings
        const { data, error } = await supabase
            .from('timeline_settings')
            .update(updates)
            .eq('timeline_id', timelineId)
            .select()
            .single();

        if (error) {
            console.error('Error updating timeline settings:', error);
            throw error;
        }

            console.log('Successfully updated timeline settings:', data);
        return data;
        } catch (error) {
            console.error('Error in updateTimelineSettings:', error);
            throw error;
        }
    }

    async getChapters(storyId: string): Promise<Chapter[]> {
        return this.getChaptersNoCache(storyId);
    }

    async getChaptersNoCache(storyId: string): Promise<Chapter[]> {
        try {
            if (!minimalLogs) {
                console.log('Fetching chapters for story:', storyId);
            }

            // Use the chapters table
            const { data, error } = await supabase
                .from('chapters')
                .select('*')
                .eq('story_id', storyId)
                // Sort only by creation date since order column doesn't exist
                .order('display_order', { ascending: true });

            if (error) {
                console.error('Error fetching chapters:', error);

                // If the table doesn't exist or no chapters found, return default chapters
                if (error.code === '42P01') {
                    console.warn('No chapters found, returning default chapters');
                    return defaultChapters;
                }

                throw error;
            }

            // Transform the data to match the Chapter interface
            const chapters: Chapter[] = (data as DBChapter[]).map(item => ({
                id: item.id,
                name: item.title || 'Untitled Chapter',
                title: item.title || 'Untitled Chapter',
                description: item.description || '', // Don't set a default description, preserve empty/null state
                created_at: item.created_at,
                updated_at: item.last_saved_at || item.created_at,
                _order: item.display_order || 0
            }));

            if (!chapters || chapters.length === 0) {
                if (!minimalLogs) {
                    console.log('No chapters found for story, returning default chapters');
                }
                return defaultChapters;
            }

            return chapters;
        } catch (error) {
            console.error('Error in getChaptersNoCache:', error);
            throw error;
        }
    }

    /**
     * Creates a timeline and all its beats in a single transaction using an RPC.
     */
    async createTimelineWithBeats(
        storyId: string,
        title: string,
        description: string,
        beats: any[]
    ): Promise<string> {
        try {
            console.log('=== Creating timeline with beats via RPC ===');
            console.log('Story ID:', storyId);
            console.log('Title:', title);
            console.log('Beats count:', beats.length);

            const { data, error } = await supabase.rpc('create_timeline_with_beats', {
                p_story_id: parseInt(storyId),
                p_title: title,
                p_description: description,
                p_beats: beats
            });

            if (error) {
                console.error('Error calling create_timeline_with_beats RPC:', error);
                throw error;
            }

            console.log('RPC result:', data);
            
            if (data && data.success && data.timeline_id) {
                return data.timeline_id.toString();
            }

            throw new Error('RPC returned without success or timeline_id');

        } catch (error) {
            console.error('Error in createTimelineWithBeats:', error);
            throw error;
        }
    }


    async updatePlotCardChapter(cardId: string, chapterId: number | null, timelineId: string): Promise<PlotCard> {
        try {
            console.log('=== Updating plot card chapter:', cardId, 'to chapter:', chapterId);

            // First, get the current chapter_id of the card
            const { data: currentCard, error: fetchError } = await supabase
                .from('plotcards')
                .select('*')
                .eq('id', cardId)
                .single();

            if (fetchError) {
                console.error('Error fetching card before update:', fetchError);
                throw fetchError;
            }

            if (!currentCard) {
                throw new Error(`Card with ID ${cardId} not found`);
            }

            const sourceChapterId = currentCard.chapter_id;
            console.log('=== Source chapter ID:', sourceChapterId);

            // Update the card with the new chapter_id
            const { data, error } = await supabase
                .from('plotcards')
                .update({
                    chapter_id: chapterId
                })
                .eq('id', cardId)
                .select()
                .single();

            if (error) {
                console.error('Error updating plot card chapter:', error);
                throw error;
            }

            if (!data) {
                throw new Error('Failed to update plot card chapter');
            }

            // Transform the data to match the PlotCard interface
            const cardData = data as DBPlotCard;
            const metadata = cardData.metadata || {};
            const plotLineId = metadata.plotLineId || cardData.timeline_id?.toString() || '0';
            const cardType = metadata.type || 'note';

            const plotCard: PlotCard = {
                id: cardData.id,
                title: cardData.title || 'Untitled',
                content: cardData.description || '',
                type: cardType as CardType,
                x: parseInt(plotLineId),
                y: cardData.chapter_id || 0, // Use chapter_id for y-coordinate
                value: cardData.description || '', // For backward compatibility
                created_at: cardData.created_at,
                updated_at: cardData.updated_at,
                chapter_id: cardData.chapter_id || null,
                position: cardData.position || null
            };

            console.log('=== Updated plot card chapter:', plotCard);
            return plotCard;
        } catch (error) {
            console.error('Error in updatePlotCardChapter:', error);
            throw error;
        }
    }

    // Method to move multiple cards to a chapter at once
    async moveCardsToChapter(cardIds: number[], chapterId: number | null, timelineId: string): Promise<PlotCard[]> {
        try {
            console.log('=== Moving cards to chapter:', chapterId, 'cards:', cardIds);

            if (!cardIds.length) {
                console.log('=== No cards to move');
                return [];
            }

            // Update all cards with the new chapter_id AND timelineId
            const { error } = await supabase
                .from('plotcards')
                .update({
                    chapter_id: chapterId,
                    timeline_id: timelineId // Also update the timeline_id
                })
                .in('id', cardIds);

            if (error) {
                console.error('Error moving cards to chapter:', error);
                throw error;
            }

            // Fetch the updated cards to return them
            const { data: updatedCards, error: fetchError } = await supabase
                .from('plotcards')
                .select('*')
                .in('id', cardIds);

            if (fetchError) {
                console.error('Error fetching updated cards:', fetchError);
                throw fetchError;
            }

            if (!updatedCards || updatedCards.length === 0) {
                console.log('=== No updated cards found');
                return [];
            }

            // Transform the data to match the PlotCard interface
            const plotCards: PlotCard[] = updatedCards.map(item => {
                const metadata = item.metadata || {};
                const plotLineId = metadata.plotLineId || item.timeline_id?.toString() || '0';
                const cardType = metadata.type || 'note';

                return {
                    id: item.id,
                    title: item.title || 'Untitled',
                    content: item.description || '',
                    type: cardType as CardType,
                    x: parseInt(timelineId), // Use the updated timeline_id as x-coordinate
                    y: item.chapter_id || 0,
                    value: item.description || '',
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    chapter_id: item.chapter_id || null,
                    position: item.position || null
                };
            });

            console.log(`=== Successfully moved ${plotCards.length} cards to chapter ${chapterId}`);
            return plotCards;
        } catch (error) {
            console.error('Error in moveCardsToChapter:', error);
            throw error;
        }
    }

    async getPlotCardsByChapter(chapterId: number, timelineId?: string, skipCache: boolean = false): Promise<PlotCard[]> {
        try {
            console.log(`=== Getting plot cards for chapter: ${chapterId} (skipCache: ${skipCache})`);

            // Build the query
            let query = supabase
                .from('plotcards')
                .select('*')
                .eq('chapter_id', chapterId)
                .order('position', { ascending: true })
                .order('created_at', { ascending: true });

            // Add timeline filter if provided
            if (timelineId) {
                query = query.eq('timeline_id', parseInt(timelineId));
            }

            // Execute the query - we can't use headers method as it's not supported in this version
            // Instead, we'll just use the regular query and rely on other cache invalidation methods
            const { data, error } = await query;

            if (error) {
                console.error('=== Error fetching plot cards for chapter:', error);
                throw error;
            }

            if (!data || data.length === 0) {
                console.log('=== No plot cards found for chapter:', chapterId);
                return [];
            }

            // Transform the data to match the PlotCard interface
            const plotCards: PlotCard[] = data.map((item: any) => {
                // Extract metadata with fallbacks
                const metadata = item.metadata || {};

                // Determine plot line (x-coordinate)
                let plotLineId = item.timeline_id.toString();

                // Determine card type from metadata, tags, or color
                let cardType: CardType = 'note'; // Default type

                if (metadata && metadata.type && typeof metadata.type === 'string') {
                    cardType = metadata.type as CardType;
                } else if (item.tags && item.tags.length > 0) {
                    if (item.tags.includes('character')) {
                        cardType = 'character';
                    } else if (item.tags.some((tag: string) => ['plot', 'event', 'location', 'battle', 'key-event'].includes(tag))) {
                        cardType = 'plot';
                    }
                } else if (item.color) {
                    // Map colors to card types as fallback (same logic as in getPlotCards)
                    switch (item.color.toLowerCase()) {
                        case '#4299e1':
                        case '#2b6cb0':
                            cardType = 'character';
                            break;
                        case '#ed8936':
                        case '#dc2626':
                        case '#718096':
                            cardType = 'plot';
                            break;
                        case '#48bb78':
                            cardType = 'plot'; // location
                            break;
                        case '#9f7aea':
                        case '#4a5568':
                            cardType = 'note';
                            break;
                        default:
                            cardType = 'note';
                    }
                }

                return {
                    id: item.id,
                    title: item.title || 'Untitled',
                    content: item.description || '',
                    type: cardType,
                    x: parseInt(plotLineId), // Use the timeline_id as x
                    y: item.chapter_id || 0, // Use chapter_id for y-coordinate
                    value: item.description || '', // For backward compatibility
                    created_at: item.created_at,
                    updated_at: item.updated_at,
                    chapter_id: item.chapter_id,
                    position: item.position || null
                };
            });

            console.log(`=== Found ${plotCards.length} plot cards for chapter ${chapterId}`);
            return plotCards;
        } catch (error) {
            console.error('=== Error in getPlotCardsByChapter:', error);
            throw error;
        }
    }

    async refreshAllData(projectId: string, storyId?: string): Promise<TimelineState> {
        try {
            console.log('=== Refreshing all timeline data for project:', projectId);

            // Use the new single-call method to get all timeline data at once
            return this.getTimelineDataSingleCall(projectId);

        } catch (error) {
            console.error('Error in refreshAllData:', error);
            throw error;
        }
    }

    async updatePlotCardPosition(
        cardId: string,
        newPosition: number,
        timelineId: string,
        chapterId: number | null
    ): Promise<PlotCard> {
        try {
            console.log('=== Updating plot card position:', cardId, 'to position:', newPosition);

            // Update the card with the new position
            const { data, error } = await supabase
                .from('plotcards')
                .update({
                    position: newPosition
                })
                .eq('id', cardId)
                .select()
                .single();

            if (error) {
                console.error('Error updating plot card position:', error);
                throw error;
            }

            // Transform the data to match the PlotCard interface
            const cardData = data as DBPlotCard;
            const metadata = cardData.metadata || {};
            const plotLineId = metadata.plotLineId || cardData.timeline_id?.toString() || '0';
            const cardType = metadata.type || 'note';

            const plotCard: PlotCard = {
                id: cardData.id,
                title: cardData.title || 'Untitled',
                content: cardData.description || '',
                type: cardType as CardType,
                x: parseInt(plotLineId),
                y: cardData.chapter_id || 0, // Use chapter_id for y-coordinate
                value: cardData.description || '', // For backward compatibility
                created_at: cardData.created_at,
                updated_at: cardData.updated_at,
                chapter_id: cardData.chapter_id || null,
                position: cardData.position || null
            };

            console.log('=== Updated plot card position:', plotCard);
            return plotCard;
        } catch (error) {
            console.error('Error in updatePlotCardPosition:', error);
            throw error;
        }
    }

    async reorderChapters(storyId: string, chapterIds: number[]): Promise<Chapter[]> {
        try {
            console.log('=== Reordering chapters for story:', storyId, chapterIds);

            // Get all chapters for this story
            const chapters = await this.getChaptersNoCache(storyId);

            if (!chapters || chapters.length === 0) {
                console.log('=== No chapters to reorder');
                return [];
            }

            // Create a map of chapter ID to chapter
            const chapterMap = new Map<number, Chapter>();
            chapters.forEach((chapter: Chapter) => {
                chapterMap.set(chapter.id, chapter);
            });

            // Validate that all chapter IDs in the reorder list exist
            for (const id of chapterIds) {
                if (!chapterMap.has(id)) {
                    throw new Error(`Chapter with ID ${id} not found`);
                }
            }

            // Call the RPC function to update the order in the database
            const { error } = await supabase.rpc('reorder_chapters', {
                p_story_id: parseInt(storyId),
                p_chapter_ids: chapterIds
            });

            if (error) {
                console.error('Error calling reorder_chapters RPC:', error);
                throw error;
            }

            console.log('=== Database update successful');

            // Return the chapters in the new order with updated _order properties
            const reorderedChapters: Chapter[] = chapterIds.map((id, index) => {
                const chapter = chapterMap.get(id)!;
                return {
                    ...chapter,
                    _order: index + 1 // display_order is 1-based in DB/RPC usually (or 0, but script used 1..N loop)
                };
            });

            return reorderedChapters;
        } catch (error) {
            console.error('Error in reorderChapters:', error);
            throw error;
        }
    }

    async createTimelineSettings(timelineId: string): Promise<TimelineSettings> {
        try {
            console.log('Creating timeline settings for timeline:', timelineId);

            // Create default settings
            const defaultSettings = await this.createDefaultSettings(timelineId);

            return defaultSettings;
        } catch (error) {
            console.error('Error in createTimelineSettings:', error);
            throw error;
        }
    }

    async reorderTimelines(storyId: string, timelineIds: number[]): Promise<PlotLine[]> {
        try {
            console.log('=== Reordering timelines for story:', storyId, timelineIds);

            // Get all timelines for this story
            const { data: allTimelines, error: timelinesError } = await supabase
                .from('timelines')
                .select('*')
                .eq('story_id', storyId);

            if (timelinesError) {
                console.error('Error fetching timelines:', timelinesError);
                throw timelinesError;
            }

            if (!allTimelines || allTimelines.length === 0) {
                console.log('=== No timelines to reorder');
                return [];
            }

            // Convert DB timelines to PlotLine objects
            const timelines: PlotLine[] = allTimelines.map(timeline => ({
                id: parseInt(timeline.id),
                name: timeline.title || 'Untitled',
                description: timeline.description || '',
                color: '#4299e1', // Default color
                created_at: timeline.created_at,
                updated_at: timeline.updated_at
            }));

            // Create a map of timeline ID to timeline
            const timelineMap = new Map<number, PlotLine>();
            timelines.forEach((timeline: PlotLine) => {
                timelineMap.set(timeline.id, timeline);
            });

            // Validate that all timeline IDs in the reorder list exist
            for (const id of timelineIds) {
                if (!timelineMap.has(id)) {
                    throw new Error(`Timeline with ID ${id} not found`);
                }
            }

            // We'll implement in-memory ordering only, since database doesn't have order column yet
            console.log('=== Using in-memory ordering only (no database update)');

            // Return the timelines in the new order with explicit _order properties
            const reorderedTimelines: PlotLine[] = timelineIds.map((id, index) => {
                const timeline = timelineMap.get(id)!;

                // Create a new timeline object with the _order property set
                const updatedTimeline = {
                    ...timeline,
                    _order: index // Explicitly assign index as the _order
                };

                return updatedTimeline;
            });

            console.log('=== Timelines reordered successfully in memory');
            console.log('=== Reordered timelines with _order:', reorderedTimelines.map(tl => ({
                id: tl.id,
                name: tl.name,
                _order: tl._order
            })));

            return reorderedTimelines;
        } catch (error) {
            console.error('Error in reorderTimelines:', error);
            throw error;
        }
    }

    /**
     * Get all timeline data in a single call using the get_secure_timeline_data_1 RPC function
     * @param projectId The project ID
     * @returns The complete timeline data
     */
    async getSecureTimelineData(projectId: string) {
        try {
            console.log(`Getting secure timeline data for project ${projectId}`);

            // Call the RPC function with just the project ID parameter
            const { data, error } = await supabase.rpc('get_secure_timeline_data_1', {
                p_project_id: projectId
            });

            if (error) {
                console.error('Error fetching secure timeline data:', error);
                throw error;
            }

            console.log('Received secure timeline data');
            return data;
        } catch (error) {
            console.error('Error in getSecureTimelineData:', error);
            throw error;
        }
    }

    /**
     * Get the complete timeline UI data in a single API call
     * This is the preferred method for getting timeline data for the UI
     * @param projectId The project ID
     * @returns The complete timeline state
     */
    async getTimelineDataSingleCall(projectId: string): Promise<TimelineState> {
        try {
            console.log('=== Fetching timeline data in a single call for project:', projectId);

            // Call the RPC function to get all data at once
            const secureData = await this.getSecureTimelineData(projectId);

            if (!secureData) {
                console.error('No data returned from secure timeline data function');
                throw new Error('No timeline data found');
            }

            // Transform the secure data into TimelineState format
            const timelineState = this.transformSecureTimelineData(secureData, projectId);

            console.log('=== Created timeline state with:', {
                timelinesCount: timelineState.plotLines.length,
                plotCardsCount: timelineState.plotCards.length,
                chaptersCount: timelineState.chapters.length
            });

            return timelineState;
        } catch (error) {
            console.error('Error in getTimelineDataSingleCall:', error);
            throw error;
        }
    }

    /**
     * Transform the secure timeline data into the TimelineState format
     * This will convert the result from get_secure_timeline_data_1 into the format expected by the Timeline component
     * @param secureData The data from get_secure_timeline_data_1
     * @param projectId The project ID
     * @returns The data in TimelineState format
     */
    transformSecureTimelineData(secureData: any, projectId: string): TimelineState {
        if (!secureData) {
            throw new Error('No secure timeline data provided');
        }

        // Get the primary timeline or first timeline
        const primaryTimeline = secureData.timeline?.find((t: any) => t.is_primary) || secureData.timeline?.[0];

        if (!primaryTimeline) {
            throw new Error('No timeline found in secure data');
        }

        // Map the timeline array to plotLines
        const plotLines = (secureData.timeline || []).map((line: any) => ({
            id: parseInt(line.id),
            name: line.title || 'Untitled',
            description: line.description || '',
            color: '#4299e1', // Default color if not provided
            created_at: line.created_at,
            updated_at: line.updated_at,
            _order: line.display_order || 0,
            isPrimary: line.is_primary
        }));

        // Map the chapters with title → name
        const chapters = (secureData.chapters || []).map((chapter: any) => ({
            id: chapter.id,
            name: chapter.title || 'Untitled Chapter',
            title: chapter.title || 'Untitled Chapter',
            description: chapter.description || '',
            created_at: chapter.created_at,
            updated_at: chapter.created_at,
            _order: 0
        }));

        // Map the plotcards with description → content and include entities and tags
        const plotCards = (secureData.plotcards || []).map((card: any) => {
            // Debug logging for entities and tags
            if (card.id % 10 === 0) { // Only log every 10th card to avoid console spam
                console.log(`Processing card ${card.id} (${card.title})`);
                console.log(`  Entities: ${JSON.stringify(card.entities)}`);
                console.log(`  Tags: ${JSON.stringify(card.tags)}`);
            }

            return {
                id: card.id,
                title: card.title || 'Untitled',
                content: card.description || '',
                type: card.metadata?.type || 'plot',
                x: parseInt(card.timeline_id),
                y: card.chapter_id || 0,
                value: card.description || '',
                created_at: card.created_at,
                updated_at: card.updated_at,
                chapter_id: card.chapter_id || null,
                position: card.position || 0,
                color: card.color,
                entities: Array.isArray(card.entities) ? card.entities : [],
                tags: Array.isArray(card.tags) ? card.tags : []
            };
        });

        // Map the plotlines to additional plotLines (they're the same as timelines in our data model)
        const plotlinesFromData = (secureData.plotlines || []).map((line: any) => ({
            id: parseInt(line.id),
            name: line.title || 'Untitled',
            description: line.description || '',
            color: '#4299e1', // Default color if not provided
            created_at: line.created_at,
            updated_at: line.updated_at,
            _order: line.display_order || 0,
            isPrimary: false
        }));

        // Combine plotLines and plotlinesFromData, removing duplicates
        const allPlotLines = [...plotLines];
        plotlinesFromData.forEach(line => {
            if (!allPlotLines.some(pl => pl.id === line.id)) {
                allPlotLines.push(line);
            }
        });

        // Get settings or use default settings
        const settings = {
            id: 'temp-' + Date.now(),
            timeline_id: primaryTimeline.id,
            zoom_level: 1,
            display_mode: 'horizontal' as const,
            show_chapter_markers: true,
            color_scheme: {
                'character': '#4299e1',
                'event': '#ed8936',
                'location': '#48bb78',
                'item': '#9f7aea',
                'note': '#f6e05e'
            }
        };

        // Create the TimelineState object
        const result: TimelineState = {
            storyId: primaryTimeline.story_id.toString(),
            projectId: projectId,
            plotLines: allPlotLines,
            chapters,
            plotCards,
            settings: settings,
            timeline: primaryTimeline
        };

        return result;
    }
}

export const timelineService = new TimelineService();